import { CollectionEditDialog, CollectionEditErrorType } from './collection-edit-dialog';
import { useCollectionEditDialog } from './collection-edit-dialog.hooks';

interface CollectionEditDialogWrapperProps {
  /**
   * The ID of the collection to edit
   *
   * @type {string}
   */
  collectionId: string;
  /**
   * The current name of the collection
   *
   * @type {string}
   */
  currentName: string;
  /**
   * The ID of the portfolio
   *
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * The ID of the organization
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * The text for the trigger button
   *
   * @optional
   * @type {string}
   */
  triggerButtonText?: string;
  /**
   * Custom trigger button to render
   * If provided, this will be used instead of the default trigger
   *
   * @optional
   * @type {React.ReactNode}
   */
  children?: React.ReactNode;
  /**
   * Callback to call when the collection is successfully renamed
   * Can be async to support waiting for mutations to complete
   *
   * @param newName - The new name of the collection
   */
  onSuccessfulRename: (newName: string) => void | Promise<void>;
}

/**
 * CollectionEditDialogWrapper component
 * Provides a dialog interface for editing collection names
 *
 * @param props - Component props
 * @returns CollectionEditDialogWrapper component
 *
 * @example
 * <CollectionEditDialog
 *   collectionId="collection-123"
 *   currentName="My Collection"
 *   portfolioId="portfolio-123"
 *   organizationId="org-123"
 *   onSuccessfulRename={(newName) => console.log('Renamed to:', newName)}
 *   onAuthenticationError={(error) => console.error(error)}
 * />
 */
function CollectionEditDialogWrapper(props: CollectionEditDialogWrapperProps) {
  const {
    collectionId,
    currentName,
    portfolioId,
    organizationId,
    isOpen,
    onOpenChange,
    hideTrigger = false,
    useIcon = false,
    triggerButtonText,
    children,
    onSuccessfulRename,
  } = props;

  const { submissionErrorMessage, onSubmit: handleSubmit } = useCollectionEditDialog({
    collectionId,
    portfolioId,
    organizationId,
    onSuccessfulRename,
  });

  const errorType = submissionErrorMessage ? CollectionEditErrorType.Generic : CollectionEditErrorType.None;

  const handleOnSubmit = async (_: string, data: { name: string }) => {
    await handleSubmit(data);
  };

  return (
    <CollectionEditDialog
      collectionId={collectionId}
      currentName={currentName}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideTrigger={hideTrigger}
      useIcon={useIcon}
      triggerButtonText={triggerButtonText}
      errorType={errorType}
      onSubmit={handleOnSubmit}
    >
      {children}
    </CollectionEditDialog>
  );
}

export { CollectionEditDialogWrapper };
